import { useStore } from '@tanstack/react-form'
import { LakehouseSyncScheduleEnum } from '@vendia/management-api-types'
import debug from 'debug'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import Card from 'src/components/containers/card'
import ListboxField from 'src/components/fields/listbox.field'
import TextField from 'src/components/fields/text.field'
import { ScrollableStepContent } from 'src/components/flows/scrollable-step-content'
import StepButtons from 'src/components/flows/step-buttons'
import { StepContentHeader } from 'src/components/flows/step-header'
import { StepWrapper } from 'src/components/flows/step-wrapper'
import { StepComponent } from 'src/components/flows/types'
import BasicModal from 'src/components/modals/modal.basic'
import SharingRulesTable from 'src/components/tables/sharing-rules-table'
import { StepId } from 'src/pages/uni-create/config'
import { processCsvFile } from 'src/utils/csv-parser'
import { isValidTableName } from 'src/utils/form/validation'
import { useFirstFocus } from 'src/utils/hooks/use-first-focus'
import { useGetUni } from 'src/utils/hooks/use-get-uni'
import { LakehouseFlowStepValues } from 'src/utils/lakehouse/types'
import { lakehouseFlowFieldValidator } from 'src/utils/lakehouse/validators'

const logger = debug('app:table-settings')

export const StepTableSettings: StepComponent<LakehouseFlowStepValues> = ({ context }) => {
  const tableNameRef = useFirstFocus()
  const navigate = useNavigate()
  const [showReprocessingModal, setShowReprocessingModal] = useState(false)
  const [isReprocessing, setIsReprocessing] = useState(false)

  const flowType = useStore(context.form.store, (s) => s.values.flowType)
  const existingVendiaTables = useStore(context.form.store, (s) => s.values.existingVendiaTables)
  const deletedVendiaTables = useStore(context.form.store, (s) => s.values.deletedVendiaTables)
  const csvConfig = useStore(context.form.store, (s) => s.values.csvConfig)
  const csvFile = useStore(context.form.store, (s) => s.values.csvFile)
  const csvOptions = useStore(context.form.store, (s) => s.values.csvOptions)

  const getUniQuery = useGetUni()
  const uni = getUniQuery.data?.getUni
  const { id: uniName, node } = useParams()
  const nodes = uni?.nodes?.filter((n) => n.name !== node)

  const tableKey = useStore(context.form.store, (s) => s.values.tableKey)

  const isValidNonExistingTableName = ({ value }: { value: string }) => {
    const nameExistsInExistingTable = existingVendiaTables?.some(
      (table) => table.name === value && (!tableKey || table.key !== tableKey),
    )

    const nameExistsInDeletedTable = deletedVendiaTables?.some(
      (table) => table.name === value && (!tableKey || table.key !== tableKey),
    )

    if (nameExistsInExistingTable || nameExistsInDeletedTable) {
      return 'Please enter a unique name that has not been used for an existing or deleted table.'
    }
    return isValidTableName({ value })
  }

  const handleBackNavigation = () => {
    // Check if this is a CSV flow and we need to restore the data
    if (csvConfig && csvFile) {
      setShowReprocessingModal(true)
      setIsReprocessing(true)

      setTimeout(async () => {
        try {
          // @ts-ignore csvOptions will be popoulated in this scenario
          await processCsvFile(csvFile, context.form, undefined, csvOptions)

          // Wait a bit longer to ensure everything is ready
          setTimeout(() => {
            setIsReprocessing(false)
            setShowReprocessingModal(false)
            requestAnimationFrame(() => {
              context.goPrevious()
            })
          }, 1000)
        } catch (error) {
          console.error('Error reprocessing CSV:', error)
          setIsReprocessing(false)
          setShowReprocessingModal(false)
          requestAnimationFrame(() => {
            context.goPrevious()
          })
        }
      }, 100)
    } else {
      context.goPrevious()
    }
  }

  return (
    <StepWrapper>
      <StepContentHeader
        large
        centered
        title='Configure your new Vendia table'
        description={`Configure your new table's name, sync frequency, and access controls`}
      ></StepContentHeader>
      <ScrollableStepContent inset autoHeight>
        <Card padding='xl' className='mx-8 w-full max-w-4xl' childrenWrapperClassName='flex flex-col gap-4'>
          <TextField
            name='product.name'
            label='Name'
            description='Provide a SQL compliant name for your table'
            ref={tableNameRef}
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.TableSettings,
              validator: isValidNonExistingTableName,
            })}
            useNestedLabel
            form={context.form}
          />
          {!csvConfig && (
            <ListboxField
              name='product.syncSchedule'
              label='Frequency (Refresh rate)'
              description='How often should this table be updated?'
              options={[
                { value: LakehouseSyncScheduleEnum.OnDemand, label: 'On-demand' },
                { value: LakehouseSyncScheduleEnum.Daily, label: 'Daily' },
                { value: LakehouseSyncScheduleEnum.Weekly, label: 'Weekly' },
                { value: LakehouseSyncScheduleEnum.Monthly, label: 'Monthly' },
              ]}
              form={context.form}
              useNestedLabel
            />
          )}

          <div className='text-neutral-8 -mb-2 text-sm'>Which partners can access this table?</div>
          {uni && nodes && (
            <SharingRulesTable
              form={context.form as any}
              partners={nodes}
              entityProperties={[]}
              hideCustom
              hideReadWrite
              viewLabel='Share *'
            />
          )}
          <div className='mt-2 text-base'>* Data transfer fidelity protected by Vendia Iceblock&trade;</div>
        </Card>
      </ScrollableStepContent>
      <context.form.Subscribe
        selector={(state) => [
          state.canSubmit,
          state.isSubmitting,
          state.values.csvConfig,
          state.values.product.selectedVendiaTables,
        ]}
      >
        {([canSubmit, isSubmitting, csvConfig, selectedVendiaTables]) => (
          <StepButtons
            context={context}
            hasPrevious={flowType === 'addTable'}
            hasCancel={true}
            onCancel={() => navigate(`/uni/${uniName}/${node}/data`)}
            isSubmitting={!!isSubmitting}
            nextBlocked={!canSubmit}
            onPrevious={handleBackNavigation}
            onNext={() => {
              if (csvConfig) {
                context.goToStep(StepId.SharingPermissions)
              } else if (Array.isArray(selectedVendiaTables) && selectedVendiaTables.length > 0) {
                context.goToStep(StepId.ColumnSharingPermissions)
              } else {
                context.goNext()
              }
            }}
          />
        )}
      </context.form.Subscribe>

      <BasicModal
        title='Restoring Column Configuration'
        isOpen={showReprocessingModal}
        onClose={() => {
          if (!isReprocessing) {
            setShowReprocessingModal(false)
          }
        }}
      >
        <div className='grid place-items-center gap-4'>
          <img className='mb-3' src='/images/sharing-files-illustration.svg' alt='Processing data' />
          <div className='text-center'>
            <p className='mb-2 font-bold'>Restoring your CSV configuration...</p>
            <p className='text-sm text-gray-600'>Please wait while we reload your column settings.</p>
          </div>
          <div className='w-full'>
            <div className='h-2 w-full overflow-hidden rounded-lg bg-gray-200'>
              <div className='h-full w-full animate-pulse bg-purple-900 opacity-50'></div>
            </div>
          </div>
        </div>
      </BasicModal>
    </StepWrapper>
  )
}
